import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import AppImage from '../../components/AppImage';
import { regionalTeamService } from '../../services/regionalTeamService';
import { regionalTeamRosterService } from '../../services/regionalTeamRosterService';
import { useAuth } from '../../contexts/AuthContext';
import {
  TANZANIA_REGION_OPTIONS,
  isRegionAdmin,
  getRegionLabel,
} from '../../utils/tanzaniaRegions';

const INITIAL_FORM = {
  teamName: '',
  region: '',
  logoFile: null,
  teamManager: '',
  headCoach: '',
  teamDoctor: '',
  teamStatistician: '',
};

const POSITION_LABELS = {
  point_guard: 'Point Guard (PG)',
  shooting_guard: 'Shooting Guard (SG)',
  small_forward: 'Small Forward (SF)',
  power_forward: 'Power Forward (PF)',
  center: 'Center (C)',
};

const statusClass = (status) => {
  const value = (status || 'pending').toLowerCase();
  if (value === 'active' || value === 'approved') return 'bg-success/10 text-success';
  if (value === 'rejected') return 'bg-destructive/10 text-destructive';
  return 'bg-muted text-muted-foreground';
};

const getPositionLabel = (value) => POSITION_LABELS[value] || value || '—';

const MemberPhoto = ({ src, alt, onOpen }) => {
  if (!src) {
    return (
      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center shrink-0">
        <Icon name="User" size={20} className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.(src, alt);
      }}
      className="w-14 h-14 rounded-full overflow-hidden border border-border shrink-0 ring-offset-background hover:ring-2 hover:ring-primary transition-all focus:outline-none focus:ring-2 focus:ring-primary"
      title="Click to view photo"
    >
      <AppImage src={src} alt={alt} className="w-full h-full object-cover" />
    </button>
  );
};

const RegionalTeamRegistration = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const regionAdmin = isRegionAdmin(userProfile?.role);
  const assignedRegion = userProfile?.region || '';
  const isGlobalAdmin = userProfile?.role === 'admin';
  const canDeleteTeams = isGlobalAdmin || regionAdmin;
  const logoInputRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [view, setView] = useState('list');
  const [editingId, setEditingId] = useState(null);

  const [teams, setTeams] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [players, setPlayers] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [photoViewer, setPhotoViewer] = useState(null);
  const [teamPendingDelete, setTeamPendingDelete] = useState(null);
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Regional Team Registration' },
  ];

  const loadTeams = useCallback(async () => {
    setIsLoadingList(true);
    setListError(null);
    try {
      const data = await regionalTeamService.getAll();
      const list = data || [];
      setTeams(
        regionAdmin && assignedRegion
          ? list.filter((team) => team.region === assignedRegion)
          : list
      );
    } catch (err) {
      setListError(err.message || 'Failed to load regional teams');
    } finally {
      setIsLoadingList(false);
    }
  }, [regionAdmin, assignedRegion]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    const loadMembers = async () => {
      if (!selectedTeamId || view !== 'list') {
        setPlayers([]);
        setOfficials([]);
        return;
      }
      setIsLoadingMembers(true);
      setMembersError(null);
      try {
        const [teamPlayers, teamOfficials] = await Promise.all([
          regionalTeamRosterService.getPlayers(selectedTeamId),
          regionalTeamRosterService.getOfficials(selectedTeamId),
        ]);
        setPlayers(teamPlayers || []);
        setOfficials(teamOfficials || []);
      } catch (err) {
        setMembersError(err.message || 'Failed to load team members');
        setPlayers([]);
        setOfficials([]);
      } finally {
        setIsLoadingMembers(false);
      }
    };
    loadMembers();
  }, [selectedTeamId, view]);

  const filteredTeams = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return teams;
    return teams.filter((team) =>
      [
        team?.teamName,
        team?.region,
        getRegionLabel(team?.region),
        team?.teamManager,
        team?.headCoach,
        team?.teamDoctor,
        team?.teamStatistician,
        team?.registrationStatus,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [teams, search]);

  const selectedTeam = useMemo(
    () => teams.find((team) => team.id === selectedTeamId) || null,
    [teams, selectedTeamId]
  );

  const clearLogoPreview = () => {
    if (logoPreview?.startsWith?.('blob:')) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
  };

  const resetForm = () => {
    clearLogoPreview();
    setFormData({
      ...INITIAL_FORM,
      region: regionAdmin ? assignedRegion : '',
    });
    setErrors({});
    setEditingId(null);
    setError(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const openAddForm = () => {
    resetForm();
    setSuccess(null);
    setView('form');
  };

  const openEditForm = (team) => {
    clearLogoPreview();
    setEditingId(team.id);
    setFormData({
      ...INITIAL_FORM,
      teamName: team.teamName || '',
      region: team.region || (regionAdmin ? assignedRegion : ''),
      teamManager: team.teamManager || '',
      headCoach: team.headCoach || '',
      teamDoctor: team.teamDoctor || '',
      teamStatistician: team.teamStatistician || '',
    });
    setLogoPreview(team.logoUrl || null);
    setErrors({});
    setError(null);
    setSuccess(null);
    setView('form');
  };

  const backToList = () => {
    resetForm();
    setView('list');
  };

  const handleSelectTeam = (teamId) => {
    setSelectedTeamId((prev) => (prev === teamId ? null : teamId));
  };

  const openPhotoViewer = (src, alt) => {
    if (!src) return;
    setPhotoViewer({ src, alt: alt || 'Member photo' });
  };

  const canDeleteTeam = (team) => {
    if (!canDeleteTeams || !team) return false;
    if (isGlobalAdmin) return true;
    if (regionAdmin && assignedRegion) {
      return team.region === assignedRegion;
    }
    return false;
  };

  const handleConfirmDeleteTeam = async () => {
    if (!teamPendingDelete?.id) return;
    setIsDeletingTeam(true);
    setListError(null);
    try {
      await regionalTeamService.delete(teamPendingDelete.id);
      setSuccess(`Deleted regional team “${teamPendingDelete.teamName || 'Unnamed'}”.`);
      if (selectedTeamId === teamPendingDelete.id) {
        setSelectedTeamId(null);
        setPlayers([]);
        setOfficials([]);
      }
      setTeamPendingDelete(null);
      await loadTeams();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setListError(err.message || 'Failed to delete regional team');
    } finally {
      setIsDeletingTeam(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (logoPreview?.startsWith?.('blob:')) URL.revokeObjectURL(logoPreview);
    setFormData((prev) => ({ ...prev, logoFile: file }));
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.teamName?.trim()) next.teamName = 'Regional team name is required';
    if (!formData.region) next.region = 'Region is required';
    if (!formData.teamManager?.trim()) next.teamManager = 'Team manager is required';
    if (!formData.headCoach?.trim()) next.headCoach = 'Head coach is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const result = editingId
        ? await regionalTeamService.update(editingId, formData)
        : await regionalTeamService.create(formData);

      if (result?.uploadWarning) {
        setSuccess(`Regional team saved, but logo upload failed (${result.uploadWarning}).`);
      } else {
        setSuccess(
          editingId
            ? 'Regional team updated successfully.'
            : 'Regional team registered successfully.'
        );
      }

      await loadTeams();
      if (result?.id) setSelectedTeamId(result.id);
      resetForm();
      setView('list');
    } catch (err) {
      setError(err.message || 'Failed to save regional team.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Map" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    Regional Team Registration
                  </h1>
                  <p className="text-muted-foreground">
                    {view === 'list'
                      ? 'Manage registered regional teams and view members'
                      : editingId
                        ? 'Update regional team details'
                        : 'Register a new regional team'}
                  </p>
                </div>
              </div>

              {view === 'list' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    iconName="UsersRound"
                    iconPosition="left"
                    onClick={() => navigate('/regional-team-players-registration')}
                  >
                    Regional Team Players
                  </Button>
                  <Button iconName="Plus" iconPosition="left" onClick={openAddForm}>
                    Add Regional Team
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={backToList}
                >
                  Back to List
                </Button>
              )}
            </div>
          </div>

          {success && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle2" size={18} />
                <p className="font-medium">{success}</p>
              </div>
            </div>
          )}

          {(error || listError) && (
            <div className="mb-6 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} />
                <p className="font-medium">{error || listError}</p>
              </div>
            </div>
          )}

          {view === 'list' ? (
            <div className="space-y-4">
              <div className="bg-card rounded-lg border border-border card-shadow p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 max-w-md">
                    <Input
                      type="search"
                      placeholder="Search by team, manager, coach..."
                      iconName="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {filteredTeams.length} of {teams.length} registered
                  </p>
                </div>
              </div>

              {isLoadingList ? (
                <div className="bg-card rounded-lg border border-border card-shadow p-12 text-center">
                  <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
                  <p className="text-muted-foreground">Loading regional teams...</p>
                </div>
              ) : filteredTeams.length === 0 ? (
                <div className="bg-card rounded-lg border border-border card-shadow p-12 text-center">
                  <Icon name="Map" size={40} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {teams.length === 0 ? 'No Regional Teams Registered' : 'No Matching Teams'}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {teams.length === 0
                      ? 'Get started by registering the first regional team.'
                      : 'Try adjusting your search.'}
                  </p>
                  {teams.length === 0 && (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <Button iconName="Plus" iconPosition="left" onClick={openAddForm}>
                        Add Regional Team
                      </Button>
                      <Button
                        variant="outline"
                        iconName="UsersRound"
                        iconPosition="left"
                        onClick={() => navigate('/regional-team-players-registration')}
                      >
                        Regional Team Players
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr className="text-left">
                            <th className="px-4 py-3 font-semibold text-foreground">Team</th>
                            <th className="px-4 py-3 font-semibold text-foreground">Region</th>
                            <th className="px-4 py-3 font-semibold text-foreground">Manager</th>
                            <th className="px-4 py-3 font-semibold text-foreground">Head Coach</th>
                            <th className="px-4 py-3 font-semibold text-foreground">Staff</th>
                            <th className="px-4 py-3 font-semibold text-foreground">Status</th>
                            <th className="px-4 py-3 font-semibold text-foreground text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTeams.map((team) => {
                            const isSelected = selectedTeamId === team.id;
                            return (
                              <tr
                                key={team.id}
                                onClick={() => handleSelectTeam(team.id)}
                                className={`border-b border-border last:border-0 cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'bg-primary/5 hover:bg-primary/10'
                                    : 'hover:bg-muted/30'
                                }`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3 min-w-[200px]">
                                    <div className="w-11 h-11 rounded-lg bg-white border border-border overflow-hidden flex items-center justify-center shrink-0">
                                      {team.logoUrl ? (
                                        <AppImage
                                          src={team.logoUrl}
                                          alt={team.teamName}
                                          className="w-full h-full object-contain p-0.5"
                                        />
                                      ) : (
                                        <Icon name="Shield" size={18} className="text-primary" />
                                      )}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-foreground truncate">
                                        {team.teamName || 'Unnamed Team'}
                                      </p>
                                      {isSelected && (
                                        <p className="text-xs text-primary">Selected · viewing members</p>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-foreground whitespace-nowrap">
                                  {getRegionLabel(team.region)}
                                </td>
                                <td className="px-4 py-3 text-foreground whitespace-nowrap">
                                  {team.teamManager || '—'}
                                </td>
                                <td className="px-4 py-3 text-foreground whitespace-nowrap">
                                  {team.headCoach || '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="min-w-[140px]">
                                    <p className="text-foreground truncate">
                                      Doc: {team.teamDoctor || '—'}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      Stats: {team.teamStatistician || '—'}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex text-xs px-2.5 py-1 rounded-full capitalize ${statusClass(
                                      team.registrationStatus
                                    )}`}
                                  >
                                    {team.registrationStatus || 'pending'}
                                  </span>
                                </td>
                                <td
                                  className="px-4 py-3 text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      iconName="UsersRound"
                                      onClick={() =>
                                        navigate(
                                          `/regional-team-players-registration?teamId=${team.id}`
                                        )
                                      }
                                    >
                                      Players
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      iconName="Pencil"
                                      iconPosition="left"
                                      onClick={() => openEditForm(team)}
                                    >
                                      Edit
                                    </Button>
                                    {canDeleteTeam(team) && (
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        iconName="Trash2"
                                        onClick={() => setTeamPendingDelete(team)}
                                      >
                                        Delete
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {selectedTeam && (
                    <div className="bg-card rounded-lg border border-border card-shadow p-6 space-y-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <MemberPhoto
                            src={selectedTeam.logoUrl}
                            alt={`${selectedTeam.teamName} logo`}
                            onOpen={openPhotoViewer}
                          />
                          <div>
                            <h2 className="text-xl font-bold text-foreground">
                              {selectedTeam.teamName}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                              Region: {getRegionLabel(selectedTeam.region)} · Team members · click
                              a photo to enlarge
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Manager: {selectedTeam.teamManager || '—'} · Coach:{' '}
                              {selectedTeam.headCoach || '—'}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="UsersRound"
                          iconPosition="left"
                          onClick={() =>
                            navigate(
                              `/regional-team-players-registration?teamId=${selectedTeam.id}`
                            )
                          }
                        >
                          Manage Roster
                        </Button>
                      </div>

                      {membersError && (
                        <div className="px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
                          {membersError}
                        </div>
                      )}

                      {isLoadingMembers ? (
                        <div className="flex items-center gap-2 text-muted-foreground py-6">
                          <Icon name="Loader2" size={18} className="animate-spin" />
                          Loading team members...
                        </div>
                      ) : (
                        <>
                          <div>
                            <h3 className="font-semibold text-foreground mb-3">
                              Players ({players.length})
                            </h3>
                            {players.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No players registered for this team yet.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {players.map((player) => (
                                  <div
                                    key={player.id}
                                    className="border border-border rounded-lg p-3 flex items-center gap-3"
                                  >
                                    <MemberPhoto
                                      src={player.photoUrl}
                                      alt={player.fullName}
                                      onOpen={openPhotoViewer}
                                    />
                                    <div className="min-w-0">
                                      <p className="font-medium text-foreground text-sm truncate">
                                        {player.fullName}
                                      </p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {getPositionLabel(player.position)}
                                        {player.age != null && player.age !== ''
                                          ? ` · Age ${player.age}`
                                          : ''}
                                      </p>
                                      {(player.email || player.phone) && (
                                        <p className="text-xs text-muted-foreground truncate">
                                          {[player.email, player.phone]
                                            .filter(Boolean)
                                            .join(' · ')}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <h3 className="font-semibold text-foreground mb-3">
                              Officials ({officials.length})
                            </h3>
                            {officials.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No other officials registered for this team yet.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {officials.map((official) => (
                                  <div
                                    key={official.id}
                                    className="border border-border rounded-lg p-3 flex items-center gap-3"
                                  >
                                    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center shrink-0">
                                      <Icon
                                        name="UserCog"
                                        size={20}
                                        className="text-muted-foreground"
                                      />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-foreground text-sm truncate">
                                        {official.fullName}
                                      </p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {official.roleTitle || 'Official'}
                                      </p>
                                      {(official.email || official.phone) && (
                                        <p className="text-xs text-muted-foreground truncate">
                                          {[official.email, official.phone]
                                            .filter(Boolean)
                                            .join(' · ')}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-4xl">
              <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
                <div className="border-b border-border px-6 py-4">
                  <h2 className="text-lg font-semibold text-foreground">
                    {editingId ? 'Update Team Details' : 'Register New Regional Team'}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Provide the regional team name, logo, and key staff
                  </p>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-1">
                      <div className="rounded-lg border border-border p-4">
                        <p className="text-sm font-medium text-muted-foreground mb-3 text-center">
                          Team Logo
                        </p>
                        <div className="aspect-square rounded-lg border border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden mb-4">
                          {logoPreview ? (
                            <AppImage
                              src={logoPreview}
                              alt="Team logo preview"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="text-center p-4">
                              <Icon
                                name="Image"
                                size={40}
                                className="text-muted-foreground mx-auto mb-2"
                              />
                              <p className="text-xs text-muted-foreground">Logo preview</p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            iconName="Upload"
                            iconPosition="left"
                            onClick={() => logoInputRef.current?.click()}
                          >
                            Choose Logo
                          </Button>
                          <span className="text-xs text-muted-foreground text-center truncate max-w-full">
                            {formData.logoFile?.name || 'No file chosen'}
                          </span>
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleLogoChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2 space-y-4">
                      <Input
                        label="Regional Team Name"
                        required
                        value={formData.teamName}
                        onChange={(e) => handleChange('teamName', e.target.value)}
                        error={errors.teamName}
                        placeholder="e.g. Dar es Salaam Regional Team"
                      />
                      <Select
                        label="Region"
                        required
                        options={TANZANIA_REGION_OPTIONS}
                        value={formData.region}
                        onChange={(value) => handleChange('region', value)}
                        error={errors.region}
                        placeholder="Select region"
                        searchable
                        disabled={regionAdmin}
                        description={
                          regionAdmin
                            ? `Assigned region: ${getRegionLabel(assignedRegion)}`
                            : undefined
                        }
                      />
                      <Input
                        label="Team Manager"
                        required
                        value={formData.teamManager}
                        onChange={(e) => handleChange('teamManager', e.target.value)}
                        error={errors.teamManager}
                        placeholder="Full name"
                      />
                      <Input
                        label="Team Head Coach"
                        required
                        value={formData.headCoach}
                        onChange={(e) => handleChange('headCoach', e.target.value)}
                        error={errors.headCoach}
                        placeholder="Full name"
                      />
                      <Input
                        label="Team Doctor"
                        value={formData.teamDoctor}
                        onChange={(e) => handleChange('teamDoctor', e.target.value)}
                        placeholder="Full name"
                      />
                      <Input
                        label="Team Statistician"
                        value={formData.teamStatistician}
                        onChange={(e) => handleChange('teamStatistician', e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={backToList}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  iconName="Save"
                  iconPosition="left"
                >
                  {editingId ? 'Save Changes' : 'Submit Registration'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>

      {teamPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !isDeletingTeam && setTeamPendingDelete(null)}
          />
          <div className="relative bg-card border border-border rounded-lg card-shadow w-full max-w-md p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                <Icon name="Trash2" size={20} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete regional team?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This permanently deletes{' '}
                  <span className="font-medium text-foreground">
                    {teamPendingDelete.teamName || 'this team'}
                  </span>
                  , including its players, officials, and related tournament fixtures. This cannot
                  be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setTeamPendingDelete(null)}
                disabled={isDeletingTeam}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                iconName="Trash2"
                iconPosition="left"
                loading={isDeletingTeam}
                onClick={handleConfirmDeleteTeam}
              >
                Delete Team
              </Button>
            </div>
          </div>
        </div>
      )}

      {photoViewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setPhotoViewer(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          <div
            className="relative bg-card rounded-lg border border-border max-w-3xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="font-medium text-foreground truncate pr-4">{photoViewer.alt}</p>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setPhotoViewer(null)}
              />
            </div>
            <div className="bg-muted/40 flex items-center justify-center p-4 min-h-[280px]">
              <AppImage
                src={photoViewer.src}
                alt={photoViewer.alt}
                className="max-h-[70vh] max-w-full object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionalTeamRegistration;
